import * as React from "react";
import { useEffect, useRef } from "react";
import { CanvasWidget } from "@projectstorm/react-canvas-core";
import "./Diagram.scss";
import { useGraph } from "../../../hooks/useGraph";
import { useAppSelector } from "../../../hooks/hooks";
import { useLazyGetCatalogQuery } from "../../libraries/DataLibrarySlice";
import { PipelineState } from "../editorSlice";

export const Diagram = () => {
  const { engine, handleDrop, zoomIn, zoomOut, deleteByItem, copyItem } = useGraph();
  const [getCatalog, { data: catalog }] = useLazyGetCatalogQuery();
  const topology = useAppSelector((state) => state.editor.current);
  const pipelineState = useAppSelector((state) => state.editor.pipelineState);
  const diagramRef = useRef<HTMLDivElement>(null);
  const useDiagramRef = React.useRef(null);

  useEffect(() => {
    if (topology && !catalog) getCatalog({ runtimeType: topology.runtimeMode });
  }, [topology, catalog, getCatalog]);

  useEffect(() => {
    const keydown = (e: KeyboardEvent) => {
      if (!diagramRef.current?.contains(document.activeElement)) return;
      const allowedStates = [
        PipelineState.DELETED,
        PipelineState.STOPPED,
        PipelineState.COMPLETED,
        PipelineState.FAILED,
      ];
      if (allowedStates.includes(pipelineState)) {
        if (e.key === "Delete") deleteByItem();
        if (e.ctrlKey && e.key.toLowerCase() === "c") copyItem();
      }
    };
    window.addEventListener("keydown", keydown);
    return () => window.removeEventListener("keydown", keydown);
  }, [pipelineState, deleteByItem, copyItem]);

  const handleMouseWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    e.deltaY > 0 ? zoomOut() : zoomIn();
  };

  if (!engine) return <div>Loading...</div>;

  return (
    <div
      ref={diagramRef}
      tabIndex={0}
      style={{ position: "relative", height: "100%", width: "100%" }}
      className="grid p-0 m-0"
      onClick={() => diagramRef.current?.focus()}
      onDrop={(e) => {
        if (
          catalog &&
          [PipelineState.DELETED, PipelineState.STOPPED, PipelineState.COMPLETED, PipelineState.FAILED].includes(
            pipelineState
          )
        ) {
          handleDrop(e, catalog);
        }
      }}
      onMouseOver={() => {
        const sel = document.getSelection();
        if (sel) sel.removeAllRanges();
      }}
      onWheel={handleMouseWheel}
      onDragOver={(event) => {
        event.preventDefault();
      }}
    >
      <CanvasWidget className="diagram-container" ref={useDiagramRef}   engine={engine} />
    </div>
  );
};
